import { Button, SchemaFormValue, Text } from '@southfields-digital/mxmz-react-ui';
import { useState } from 'react';

import {
  disableAutoUpdate,
  enableAutoUpdate,
  useAutoUpdateSnapshot,
  type AutoUpdateConfig,
} from '../../application/autoUpdateManager';
import { useDataConnector } from '../../hooks';
import { usePreviewData } from '../../hooks/usePreviewData';

import { AutoUpdateModal } from './AutoUpdateModal';
import styles from './DataConnectorPanel.module.scss';
import { ErrorDisplay } from './ErrorDisplay';
import { LoadingSection } from './LoadingSection';
import { PreviewSection } from './PreviewSection';
import { RuntimeFieldsSection } from './RuntimeFieldsSection';

type DataConnectorPanelProps = {
  selectedGraphic: PlaylistGraphic;
  onImport: (propertyValues: PropertyValue) => void;
};

export default function DataConnectorPanel({ selectedGraphic, onImport }: DataConnectorPanelProps) {
  // keep track of runtimecontext select
  const [selectedRuntimeContext, setSelectedRuntimeContext] = useState<SchemaFormValue>({});

  const { state, designTimeContext, schemaFormData, fetchData, canFetch, schema, mergedContext } =
    useDataConnector({
      playlistGraphic: selectedGraphic,
    });

  const preview = usePreviewData({
    graphic: selectedGraphic.graphic,
    rawData: state.status === 'success' ? state.data : undefined,
    currentPropertyValues: selectedGraphic.propertiesValues,
  });

  const isLoading = state.status === 'fetching' || state.status === 'loading-schema';
  const error = state.status === 'error' ? state.error : undefined;

  const dataConnector = selectedGraphic.graphic.config.dataConnector;
  const subscribePath = dataConnector?.endpoint?.subscribePath;
  const autoUpdateAvailable = Boolean(subscribePath);
  const autoUpdateSnapshot = useAutoUpdateSnapshot(
    autoUpdateAvailable ? selectedGraphic.id : undefined
  );
  const autoUpdateEnabled = autoUpdateSnapshot.isEnabled;

  const [isAutoUpdateModalOpen, setAutoUpdateModalOpen] = useState(false);
  const [autoUpdateForm, setAutoUpdateForm] = useState<AutoUpdateConfig>(autoUpdateSnapshot.config);

  const handleImport = () => {
    if (autoUpdateEnabled) {
      return;
    }
    const injectedValues = preview.getInjectedValues();
    onImport(injectedValues);
    preview.clearPreview();
  };

  const handleUpdateProperty = (propertyId: string, value: string | PropertyValue[]) => {
    if (autoUpdateEnabled) {
      return;
    }
    preview.updatePreview({ propertyId, value });
  };

  const handleFetchData = () => {
    fetchData(selectedRuntimeContext);
  };

  const handleOpenAutoUpdateModal = () => {
    setAutoUpdateForm(autoUpdateSnapshot.config);
    setAutoUpdateModalOpen(true);
  };

  const handleCloseAutoUpdateModal = () => {
    setAutoUpdateModalOpen(false);
  };

  const handleAutoUpdateFormChange = (values: AutoUpdateConfig) => {
    setAutoUpdateForm(values);
  };

  const handleSaveAutoUpdate = () => {
    if (!subscribePath) {
      return;
    }

    enableAutoUpdate({
      playlistGraphicId: selectedGraphic.id,
      subscribePath,
      context: mergedContext,
      config: autoUpdateForm,
    });
    setAutoUpdateModalOpen(false);
  };

  const handleDisableAutoUpdate = () => {
    disableAutoUpdate(selectedGraphic.id);
    setAutoUpdateModalOpen(false);
  };

  if (dataConnector === undefined) {
    return (
      <div className={styles.Container}>
        <Text as="p">This graphic does not have a data connector configured.</Text>
      </div>
    );
  }

  const runtimeFieldsDisabled = isLoading || autoUpdateEnabled;
  const isFetchDisabled = !canFetch || isLoading || autoUpdateEnabled;

  return (
    <div className={styles.Container}>
      <Text as="h2" className={styles.Title}>
        Data Connector
      </Text>

      {!!error && <ErrorDisplay error={error} />}

      <div className={styles.Content}>
        {schema && (
          <RuntimeFieldsSection
            schema={schema}
            formData={schemaFormData}
            designTimeContext={designTimeContext}
            runtimeContext={selectedRuntimeContext}
            onContextChange={setSelectedRuntimeContext}
            isDisabled={runtimeFieldsDisabled}
          />
        )}

        <div className={styles.ActionRow}>
          {!autoUpdateEnabled && (
            <Button variant="primary" onClick={handleFetchData} disabled={isFetchDisabled}>
              Fetch Data
            </Button>
          )}

          {autoUpdateAvailable && (
            <Button
              variant={autoUpdateEnabled ? 'danger' : 'secondary'}
              onClick={autoUpdateEnabled ? handleDisableAutoUpdate : handleOpenAutoUpdateModal}
              className={autoUpdateEnabled ? styles.AutoUpdateButtonActive : undefined}
            >
              {autoUpdateEnabled ? 'Disable auto-update' : 'Auto-update'}
            </Button>
          )}
        </div>

        {autoUpdateAvailable && (
          <div className={styles.AutoUpdateStatus}>
            <Text as="span">
              {autoUpdateEnabled
                ? `Auto-update ${autoUpdateSnapshot.status}`
                : 'Auto-update disabled'}
            </Text>
            {autoUpdateSnapshot.error && (
              <Text as="span" className={styles.AutoUpdateError}>
                {autoUpdateSnapshot.error}
              </Text>
            )}
            {autoUpdateEnabled && autoUpdateSnapshot.status === 'connected' && (
              <div className={styles.AutoUpdateConfig}>
                <Text as="span">Interval: {autoUpdateSnapshot.config.interval}s</Text>
                <Text as="span">
                  Push when on-air: {autoUpdateSnapshot.config.pushWhenOnAir ? 'Yes' : 'No'}
                </Text>
              </div>
            )}
          </div>
        )}

        {isLoading && <LoadingSection />}

        {!autoUpdateEnabled && preview.previewData && (
          <PreviewSection
            graphic={selectedGraphic.graphic}
            previewData={preview.previewData}
            onImport={handleImport}
            onUpdateProperty={handleUpdateProperty}
            isImportDisabled={autoUpdateEnabled}
          />
        )}
      </div>

      {autoUpdateAvailable && (
        <AutoUpdateModal
          isOpen={isAutoUpdateModalOpen}
          values={autoUpdateForm}
          onChange={handleAutoUpdateFormChange}
          onClose={handleCloseAutoUpdateModal}
          onSave={handleSaveAutoUpdate}
          onDisable={handleDisableAutoUpdate}
          canDisable={autoUpdateSnapshot.isEnabled}
        />
      )}
    </div>
  );
}
